import { FolderKind } from '@mimir/common/dto/folderModels';
import type { LayoutFeatures } from './model/layoutFeatures';
import { RouteName } from './constants';
import type { Route } from 'vue-router/types/router';

const defaultPathFeatures: LayoutFeatures = {
  rich: true
};

const exportLayoutFeatures: LayoutFeatures = {
  export: true
};

const browseLayoutFeatures: LayoutFeatures = {
  browse: true
};

const adminNav = {
  title: 'Admin',
  links: [
    {
      text: 'Dashboard',
      to: { name: RouteName.ADMIN }
    },
    {
      text: 'System Tasks',
      to: { name: RouteName.ADMIN_SYSTEM_TASKS }
    },
    {
      text: 'SSO Configuration',
      to: { name: RouteName.SSO_CONFIGURATION }
    }
  ]
};

/**
 *  This used to be implemented by beforeRouteEnter in SearchItem.vue which
 *  at that time was the route component. Now SearchPage.vue is the route
 *  component and does not support beforeRouteEnter since it is using
 *  composition API. Because of that we need to implement the logic here.
 */
function setSkipRefreshOnLoad(to: Route, from: Route) {
  // Skip refresh when for instance returning form itemDetails page
  const skipRefreshOnLoad = (from && from.name && from.name !== 'searchItem' && from.name !== 'folderView' && to.query.reload === 'false');
  // Note: It seems that meta always exists even if there is not meta
  // set in the route definition. So it seems the router normalizes it
  // to { } if not set.
  if (to.meta) {
    to.meta.skipRefreshOnLoad = skipRefreshOnLoad;
  }
}

const routes = [
  {
    path: '/',
    component: () => import('./pages/layout/Layout.vue'),
    props: {
      features: defaultPathFeatures,
    },
    children: [
      {
        path: '/',
        alias: '/search-item',
        name: RouteName.SEARCH_ITEM,
        component: () => import('./pages/search/SearchPage.vue'),
        meta: { tab: 'browse' },
        // @ts-expect-error types differ between vue-router and vue-router-next
        beforeEnter: (to, from, next) => {
          setSkipRefreshOnLoad(to, from);
          next();
        },
      },
      {
        path: '/sequence',
        name: RouteName.CUTTER,
        component: () => import('./pages/cutter/CutterPage.vue'),
        props: (route: Route) => ({ cpId: route.params.cpId, boxId: route.params.boxId, sequenceId: route.params.sequenceId }),
        children: [
          {
            path: '',
            name: RouteName.SOURCE_BROWSER,
            component: () => import('./pages/search/SearchItem.vue'),
            meta: { tab: 'cutter' },
          },
          {
            path: ':sequenceId',
            name: RouteName.SEQUENCE_PROPERTIES,
            component: () => import('./pages/cutter/SequenceProperties.vue'),
            props: true,
            meta: { tab: 'cutter' },
          },
          {
            path: ':sequenceId/cp/:cpId',
            name: RouteName.CONTROL_POINT_EDITOR,
            component: () => import('./panes/ControlPointEditor.vue'),
            props: true,
            meta: { tab: 'cutter' },
          },
          {
            path: ':sequenceId/box/:boxId',
            name: RouteName.BOX_PROPERTIES,
            component: () => import('./pages/cutter/BoxProperties.vue'),
            props: true,
            meta: { tab: 'cutter' },
          },
          {
            path: ':sequenceId/box/:boxId/effects',
            name: 'boxEffects',
            component: () => import('./panes/BoxEffectsPane.vue'),
            props: true,
            meta: { tab: 'cutter' },
          },
          {
            path: ':sequenceId/box/:boxId/effect/:effectId',
            name: 'effectProperties',
            component: () => import('./panes/EffectPropertiesPane.vue'),
            props: true,
            meta: { tab: 'cutter' },
          },
          {
            path: ':sequenceId/box/:boxId/effect/:effectId/cp/:effectCpId',
            name: 'effectControlPointEditor',
            component: () => import('./panes/EffectControlPointEditor.vue'),
            props: true,
            meta: { tab: 'cutter' },
          },
          {
            path: 'item/:itemId',
            name: RouteName.ITEM_PROPERTIES,
            component: () => import('./pages/cutter/ItemProperties.vue'),
            props: true,
            meta: { tab: 'cutter' },
          },
        ],
      },
      {
        path: '/index.html',
        redirect: { name: RouteName.SEARCH_ITEM },
      },
      {
        path: '/search-person',
        redirect: { name: RouteName.SEARCH_ITEM }, // For old bookmarks containing search-person
      },
      {
        path: '/share',
        name: RouteName.SHARE,
        component: () => import('@mimir/web/pages/share/MyShares.vue'),
      },
      {
        path: '/userTasks',
        name: RouteName.USER_TASKS,
        component: () => import('@mimir/web/pages/tasks/MyTasks.vue'),
      },
      {
        path: '/activity',
        name: RouteName.ACTIVITY,
        component: () => import('@mimir/web/pages/activity/ActivityMonitor.vue'),
        meta: { tab: 'activity' },
      },
      {
        path: '/scheduler',
        name: RouteName.SCHEDULER,
        component: () => import('@mimir/web/pages/scheduler/SchedulePage.vue'),
        meta: { tab: 'scheduler' },
      },
      {
        path: '/playout',
        name: RouteName.PLAYOUT,
        component: () => import('@mimir/web/pages/playout/PlayoutPage.vue'),
      },
      {
        path: '/premiere',
        name: RouteName.PREMIERE,
        component: () => import('@mimir/web/pages/premiere/ProjectSyncing.vue'),
      },
      {
        /*VUE3*/// path: '/settings/:pathMatch(.*)*',
        /*VUE2*/ path: '/settings*',
        name: RouteName.SETTINGS,
        component: () => import('@mimir/web/pages/settings/Settings.vue'),
      },
      {
        path: '/my-account',
        redirect: { name: RouteName.MY_ORGANIZATION}, // For old bookmarks containing my-account
      },
      {
        path: '/my-organisation',
        name: RouteName.MY_ORGANIZATION,
        component: () => import('@mimir/web/pages/user/MyOrganization.vue'),
      },
      {
        path: '/changelog',
        name: RouteName.CHANGELOG,
        component: () => import('@mimir/web/pages/user/Changelog.vue'),
        props: () => ({
          staticHeightInPx: 52 + 32 + 52, // header, top padding, settings header
        }),
      },
      {
        path: '/user-settings',
        name: RouteName.USER_SETTINGS,
        component: () => import('@mimir/web/pages/user/UserSettings.vue'),
      },
      {
        path: '/desktop-settings',
        name: RouteName.DESKTOP_SETTINGS,
        component: () => import('@mimir/web/pages/user/DesktopSettings.vue'),
      },
      {
        path: '/ame-settings',
        name: RouteName.AME_SETTINGS,
        component: () => import('@mimir/web/pages/user/AmeSettings.vue'),
      },
      {
        path: '/dvr-settings',
        name: RouteName.DVR_SETTINGS,
        component: () => import('@mimir/web/pages/user/DvrSettings.vue'),
      },
      {
        path: '/logging-settings',
        name: RouteName.LOGGING_SETTINGS,
        component: () => import('@mimir/web/pages/user/LoggingSettings.vue'),
      },
      {
        path: '/item-details/:id',
        name: RouteName.ITEM_DETAILS,
        component: () => import('@mimir/web/pages/item/ItemDetails.vue'),
        // @ts-expect-error types differ between vue-router and vue-router-next
        props: route => ({
          itemId: route.params.id,
        }),
        meta: { tab: 'browse' },
      },
      {
        path: '/person-details/:id',
        name: RouteName.PERSON_DETAILS,
        component: () => import('@mimir/web/pages/person/PersonDetails.vue'),
      },
      {
        path: '/admin',
        name: RouteName.ADMIN,
        component: () => import('@mimir/web/pages/admin/Overview.vue'),
        meta: {
          breadcrumbs: [
            {
              text: 'Admin',
              disabled: true,
            },
            {
              text: 'Dashboard',
              disabled: true,
            }
          ],
          nav: adminNav,
        }
      },
      {
        path: '/admin/systemTasks',
        name: RouteName.ADMIN_SYSTEM_TASKS,
        component: () => import('@mimir/web/pages/admin/SystemTaskList.vue'),
        meta: {
          breadcrumbs: [
            {
              text: 'Admin',
              disabled: true,
            },
            {
              text: 'System Tasks',
              disabled: true,
            }
          ],
          basicLayout: true,
          nav: adminNav,
        }
      },
      {
        path: '/admin/SSOConfiguration',
        name: RouteName.SSO_CONFIGURATION,
        component: () => import('@mimir/web/pages/admin/SSOConfiguration.vue'),
        meta: {
          breadcrumbs: [
            {
              text: 'Admin',
              disabled: true,
            },
            {
              text: 'SSO Configuration',
              disabled: true,
            }
          ],
          basicLayout: true,
          nav: adminNav,
        }
      },
      {
        path: '/admin/systemTasks/start',
        name: RouteName.ADMIN_SYSTEM_TASKS_START,
        component: () => import('@mimir/web/pages/admin/StartSystemTask.vue'),
        meta: {
          breadcrumbs: [
            {
              text: 'Admin',
              disabled: true,
            },
            {
              text: 'System Tasks',
              name: RouteName.ADMIN_SYSTEM_TASKS_START,
              disabled: false,
              to: { name: RouteName.ADMIN_SYSTEM_TASKS },
              exact: true
            },
            {
              text: 'Start',
              disabled: true,
            }
          ],
          basicLayout: true,
          nav: adminNav,
        }
      },
      {
        path: '/support',
        name: RouteName.SUPPORT,
        component: () => import('@mimir/web/pages/support/SupportDashboard.vue'),
      },
      {
        path: 'folders',
        name: RouteName.FOLDER_VIEW,
        component: () => import('./pages/search/SearchPage.vue'),
        // @ts-expect-error types differ between vue-router and vue-router-next
        props: route => ({
          folderId: route.query.folderId,
        }),
        // @ts-expect-error types differ between vue-router and vue-router-next
        beforeEnter: (to, from, next) => {
          setSkipRefreshOnLoad(to, from);

          if (to.query.folderId) {
            next();
            return;
          }

          const defaultQuery = { folderId: FolderKind.TenantRoot };
          next({
            name: RouteName.FOLDER_VIEW,
            query: { ...to.query, ...defaultQuery },
          });
        },
        meta: { tab: 'browse' },
      },
      {
        path: '/recycle-bin',
        name: RouteName.RECYCLE_BIN,
        component: () => import('@mimir/web/pages/search/SearchPage.vue'),
        children: [],
        // @ts-expect-error types differ between vue-router and vue-router-next
        beforeEnter: (to, from, next) => {
          setSkipRefreshOnLoad(to, from);
          next();
        }
      },
      {
        path: '/change-password',
        name: RouteName.CHANGE_PASSWORD,
        component: () => import('@mimir/web/pages/user/ChangePassword.vue'),
        children: [],
      },
      {
        path: '/local-files',
        name: 'localFiles',
        component: () => import('@mimir/web/pages/localFiles/LocalFileBrowser.vue'),
        // @ts-expect-error types differ between vue-router and vue-router-next
        props: route => ({
          folderId: route.query.folderId,
        }),
        children: [],
      },
    ],
  },
  {
    path: '/export',
    name: RouteName.EXPORT,
    component: () => import('./pages/layout/Layout.vue'),
    props: {
      features: exportLayoutFeatures
    }
  },
  {
    path: '/browse',
    name: RouteName.BROWSE,
    component: () => import('./pages/layout/Layout.vue'),
    props: {
      features: browseLayoutFeatures
    },
  },
  {
    path: '/mos',
    name: RouteName.MOS,
    component: () => import('./pages/mos/MosHostExampleLayout.vue'),
  },
  {
    path: '/mosGraphics',
    name: RouteName.MOS_GRAPHICS,
    component: () => import('./pages/mos/MosGraphicsHostLayout.vue'),
  },
  {
    path: '/drag',
    name: RouteName.DRAG,
    component: () => import('./pages/layout/DragTest.vue'),
  },
  {
    path: '/framedrag',
    name: RouteName.FRAME_DRAG,
    component: () => import('./pages/layout/FrameDragTest.vue'),
  },
  {
    path: '/public',
    name: RouteName.PUBLIC,
    component: () => import('@mimir/web/pages/layout/Blank.vue'),
    children: [
      {
        path: 'forgot-password',
        name: RouteName.FORGOT_PASSWORD,
        component: () => import('@mimir/web/pages/public/ForgotPassword.vue'),
      },
      {
        path: 'forgot-password/verify',
        name: RouteName.FORGOT_PASSWORD_VERIFY,
        component: () => import('@mimir/web/pages/public/ForgotPassword.vue'),
        props: {
          verify: true
        }
      },
      {
        path: 'forgot-password/reset',
        name: RouteName.FORGOT_PASSWORD_RESET,
        component: () => import('@mimir/web/pages/public/ForgotPassword.vue'),
        props: {
          afterReset: {
            path: '/'
          }
        }
      }
    ],
  },
  {
    path: '/dev',
    name: RouteName.BARE_DEV,
    component: () => import('@mimir/web/pages/bare/BareDev.vue'),
  },
  {
    path: '/sign-in-code',
    name: RouteName.SIGN_IN_CODE,
    component: () => import('@mimir/web/pages/bare/SignInCode.vue'),
    // @ts-expect-error types differ between vue-router and vue-router-next
    props: route => ({ code: route.query.code })
  }
];

export default routes;
